import React from "react";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { Card } from "@/components/ui/card";
import { CheckCircle2, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";

export default function About() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
      <Seo
        title="Über Lactoseintoleranz"
        description="Lactoseintoleranz erklärt: Was passiert im Körper, wie häufig ist der Zustand und warum ist er keine Krankheit. Evidenzbasierte Grundlagen."
        keywords="Lactoseintoleranz Ursachen, Lactase-Mangel, MCM6-Gen, Milchzuckerverdauung"
        path="/ueber-lactoseintoleranz"
      />
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Grundlagen</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Über Lactoseintoleranz</h1>
      <p className="text-lg text-slate-600 mt-5 leading-relaxed">
        Lactoseintoleranz beschreibt die verminderte Fähigkeit, Milchzucker (Lactose) im Dünndarm aufzuspalten. Es ist ein Zustand - keine Krankheit.
      </p>

      <ImagePlaceholder src="/plan-images/slide02-02.webp" label="Erklärgrafik: Lactase im Dünndarm" ratio="aspect-[16/9]" className="my-10" />

      <div className="prose max-w-none">
        <h2 className="text-2xl font-semibold text-slate-900 mt-10 mb-4">Was passiert im Körper?</h2>
        <p className="text-slate-600 leading-relaxed">
          Das Enzym Lactase spaltet den Milchzucker in seine Bestandteile Glucose und Galactose auf. Bei Lactoseintoleranz wird zu wenig Lactase produziert. Unverdaute Lactose gelangt in den Dickdarm, wo Bakterien sie fermentieren - Blähungen, Bauchschmerzen oder Durchfall können folgen.
        </p>

        <h2 className="text-2xl font-semibold text-slate-900 mt-10 mb-4">Wie häufig ist das?</h2>
        <p className="text-slate-600 leading-relaxed">
          Weltweit sind rund <strong>75 %</strong> aller Erwachsenen von einer verminderten Lactase-Produktion betroffen. In Nordeuropa liegt der Anteil bei etwa 15 %, in Teilen Asiens und Afrikas bei über 90 %. Es ist damit der genetisch häufigste Zustand der Menschheit.
        </p>

        <h2 className="text-2xl font-semibold text-slate-900 mt-10 mb-4">Die gute Nachricht</h2>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <Card className="p-6 rounded-2xl border-emerald-100 bg-emerald-50/40">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-3" />
            <div className="font-semibold text-slate-900 mb-2">Kleine Mengen sind kein Problem</div>
            <p className="text-sm text-slate-600">Die meisten Betroffenen vertragen bis zu 12 g Lactose pro Mahlzeit ohne Beschwerden.</p>
          </Card>
          <Card className="p-6 rounded-2xl border-emerald-100 bg-emerald-50/40">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-3" />
            <div className="font-semibold text-slate-900 mb-2">Toleranz ist trainierbar</div>
            <p className="text-sm text-slate-600">Der Darm passt sich an. Mit dem richtigen Aufbau lässt sich Toleranz zurückgewinnen.</p>
          </Card>
        </div>

        <h2 className="text-2xl font-semibold text-slate-900 mt-12 mb-4">Wann sollte man einen Arzt aufsuchen?</h2>
        <Card className="p-6 rounded-2xl border-amber-100 bg-amber-50/40">
          <AlertCircle className="w-6 h-6 text-amber-600 mb-3" />
          <p className="text-sm text-slate-700 leading-relaxed">
            Bei starken, anhaltenden Beschwerden - insbesondere ungewolltem Gewichtsverlust, Blut im Stuhl oder Fieber - sollte immer eine ärztliche Abklärung erfolgen. Ähnliche Symptome können auch bei Reizdarm, Milcheiweißallergie oder anderen Erkrankungen auftreten.
          </p>
        </Card>
      </div>
    </div>
  );
}
